import React, { useContext, useState } from 'react'
import { useHistory } from 'react-router'
import Compressor from 'compressorjs'
import { HicetnuncContext } from '../../context/HicetnuncContext'
import { Page, Container, Padding } from '../../components/layout'
import { Input } from '../../components/input'
import { Button, Curate, Primary } from '../../components/button'
import { Upload } from '../../components/upload'
import { Preview } from '../../components/preview'
import { prepareFile, prepareDirectory } from '../../data/ipfs'
import { prepareFilesFromZIP } from '../../utils/html'
import {
  ALLOWED_MIMETYPES,
  ALLOWED_FILETYPES_LABEL,
  ALLOWED_COVER_MIMETYPES,
  ALLOWED_COVER_FILETYPES_LABEL,
  MINT_FILESIZE,
  MIMETYPE,
  MAX_EDITIONS,
  MIN_ROYALTIES,
  MAX_ROYALTIES,
} from '../../constants'

const coverOptions = {
  quality: 0.85,
  maxWidth: 1024,
  maxHeight: 1024,
}

const thumbnailOptions = {
  quality: 0.85,
  maxWidth: 350,
  maxHeight: 350,
}

// @crzypathwork change to "true" to activate displayUri and thumbnailUri
const GENERATE_DISPLAY_AND_THUMBNAIL = true

export const Helper = () => {
  const { mint_n_pay, getAuth, acc, setAccount, setFeedback, syncTaquito, setMenu } =
    useContext(HicetnuncContext)
  const history = useHistory()

  let md = window.tz.mintData;

  const [step, setStep] = useState(1)
  const [title, setTitle] = useState(md.title)
  const [description, setDescription] = useState(md.description)
  const [tags, setTags] = useState(md.tags)
  const [amount, setAmount] = useState(1)
  const [royalties, setRoyalties] = useState(md.royalties)
  const [file, setFile] = useState(md.file) // the uploaded file
  const [cover, setCover] = useState() // the uploaded or generated cover image
  const [thumbnail, setThumbnail] = useState() // the uploaded or generated cover image
  const [needsCover, setNeedsCover] = useState(false)

  const handleHelper = async () => {
    if (!acc) {
      // warning for sync
      setFeedback({
        visible: true,
        message: 'sync your wallet',
        progress: true,
        confirm: false,
      })

      await syncTaquito()

      setFeedback({
        visible: false,
      })
    } else {
      await setAccount()

      // check mime type
      if (ALLOWED_MIMETYPES.indexOf(file.mimeType) === -1) {
        // alert(
        //   `File format invalid. supported formats include: ${ALLOWED_FILETYPES_LABEL.toLocaleLowerCase()}`
        // )

        setFeedback({
          visible: true,
          message: `File format invalid. supported formats include: ${ALLOWED_FILETYPES_LABEL.toLocaleLowerCase()}`,
          progress: false,
          confirm: true,
          confirmCallback: () => {
            setFeedback({ visible: false })
          },
        })

        return
      }

      // check file size
      const filesize = (file.file.size / 1024 / 1024).toFixed(4)
      if (filesize > MINT_FILESIZE) {
        // alert(
        //   `File too big (${filesize}). Limit is currently set at ${MINT_FILESIZE}MB`
        // )

        setFeedback({
          visible: true,
          message: `File too big (${filesize}). Limit is currently set at ${MINT_FILESIZE}MB`,
          progress: false,
          confirm: true,
          confirmCallback: () => {
            setFeedback({ visible: false })
          },
        })

        return
      }

      // file about to be minted, change to the mint screen
      setStep(2)

      setFeedback({
        visible: true,
        message: 'preparing OBJKT',
        progress: true,
        confirm: false,
      })

      // upload file(s)
      let nftCid
      if (
        [MIMETYPE.ZIP, MIMETYPE.ZIP1, MIMETYPE.ZIP2].includes(file.mimeType)
      ) {
        const files = await prepareFilesFromZIP(file.buffer)

        nftCid = await prepareDirectory({
          name: title,
          description,
          tags,
          address: acc.address,
          files,
          cover,
          thumbnail,
          generateDisplayUri: GENERATE_DISPLAY_AND_THUMBNAIL,
        })
      } else {
        // process all other files
        nftCid = await prepareFile({
          name: title,
          description,
          tags,
          address: acc.address,
          buffer: file.buffer,
          mimeType: file.mimeType,
          cover,
          thumbnail,
          generateDisplayUri: GENERATE_DISPLAY_AND_THUMBNAIL,
        })
      }

      console.log("generting metropolis mint " + nftCid.path)
      console.log(nftCid)
      mint_n_pay(getAuth(), amount, nftCid.path, royalties, md.mint_fee)
    }
  }

  const handlePreview = () => {
    setStep(1)
  }

  const handleFileUpload = async (props) => {
    // console.log(props)
    setFile(props)

    if (GENERATE_DISPLAY_AND_THUMBNAIL) {
      if (props.mimeType.indexOf('image') === 0) {
        setNeedsCover(false)
        await generateCoverAndThumbnail(props)
      } else {
        setNeedsCover(true)
      }
    }
  }

  const generateCompressedImage = async (props, options) => {
    const blob = await compressImage(props.file, options)
    const mimeType = blob.type
    const buffer = await blob.arrayBuffer()
    const reader = await blobToDataURL(blob)
    return { mimeType, buffer, reader }
  }

  const compressImage = (file, options) => {
    return new Promise(async (resolve, reject) => {
      new Compressor(file, {
        ...options,
        success(blob) {
          resolve(blob)
        },
        error(err) {
          reject(err)
        },
      })
    })
  }

  const blobToDataURL = async (blob) => {
    return new Promise((resolve, reject) => {
      let reader = new FileReader()
      reader.onerror = reject
      reader.onload = (e) => resolve(reader.result)
      reader.readAsDataURL(blob)
    })
  }

  const handleCoverUpload = async (props) => {
    await generateCoverAndThumbnail(props)
  }

  const generateCoverAndThumbnail = async (props) => {
    // TMP: skip GIFs to avoid making static
    if (props.mimeType === MIMETYPE.GIF) {
      setCover(props)
      setThumbnail(props)
      return
    }

    const cover = await generateCompressedImage(props, coverOptions)
    setCover(cover)

    const thumb = await generateCompressedImage(props, thumbnailOptions)
    setThumbnail(thumb)
  }

  const limitNumericField = async (target, minValue, maxValue) => {
    if (target.value === '') target.value = '' // Seems redundant but actually cleans up e.g. '234e'
    target.value = Math.round(
      Math.max(Math.min(target.value, maxValue), minValue)
    )
  }

  const handleValidation = () => {
    if (
      amount <= 0 ||
      amount > MAX_EDITIONS ||
      royalties < MIN_ROYALTIES ||
      royalties > MAX_ROYALTIES ||
      !file
    ) {
      return true
    }
    if (GENERATE_DISPLAY_AND_THUMBNAIL) {
      if (cover && thumbnail) {
        return false
      }
    } else {
      return false
    }
    return true
  }

  const handleHomeRecovery = () => {
    history.push("/")
    setTimeout(() => {
      console.log("Returning home")
      window.location.hash = "#recover"
    }, 100)
  }

  if(file && !cover) {
    generateCoverAndThumbnail(file)
  }
  return (
    <Page title="helper" large>
      {step === 0 && (
        <>
          <Container>
            <Padding>
              <Input
                type="text"
                onChange={(e) => setTitle(e.target.value)}
                placeholder="title"
                label="title"
                value={title}
              />

              <Input
                type="text"
                style={{ whiteSpace: 'pre' }}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="description"
                label="description"
                maxlength="5000"
                value={description}
              />

              <Input
                type="text"
                onChange={(e) => setTags(e.target.value)}
                placeholder="tags (comma separated. example: illustration, digital)"
                label="tags"
                value={tags}
              />

              <Input
                type="number"
                min={1}
                max={MAX_EDITIONS}
                onChange={(e) => setAmount(e.target.value)}
                onBlur={(e) => {
                  limitNumericField(e.target, 1, MAX_EDITIONS)
                  setAmount(e.target.value)
                }}
                placeholder={`editions (no. editions, 1-${MAX_EDITIONS})`}
                label="editions"
                value={amount}
              />

              <Input
                type="number"
                min={MIN_ROYALTIES}
                max={MAX_ROYALTIES}
                onChange={(e) => setRoyalties(e.target.value)}
                onBlur={(e) => {
                  limitNumericField(e.target, MIN_ROYALTIES, MAX_ROYALTIES)
                  setRoyalties(e.target.value)
                }}
                placeholder={`royalties after each sale (between ${MIN_ROYALTIES}-${MAX_ROYALTIES}%)`}
                label="royalties"
                value={royalties}
              />
            </Padding>
          </Container>

          <Container>
            <Padding>
              <Upload
                label="Upload OBJKT"
                allowedTypesLabel={ALLOWED_FILETYPES_LABEL}
                onChange={handleFileUpload}
              />
            </Padding>
          </Container>

          {file && needsCover && (
            <Container>
              <Padding>
                <Upload
                  label="Upload cover image"
                  allowedTypes={ALLOWED_COVER_MIMETYPES}
                  allowedTypesLabel={ALLOWED_COVER_FILETYPES_LABEL}
                  onChange={handleCoverUpload}
                />
              </Padding>
            </Container>
          )}

          <Container>
            <Padding>
              <Button onClick={handlePreview} fit disabled={handleValidation()}>
                <Curate>Preview</Curate>
              </Button>
            </Padding>
          </Container>
        </>
      )}

      {step === 1 && (
        <>
          <Container>
            <Padding>
              <div style={{ display: 'flex' }}>
                <Button onClick={handleHomeRecovery} fit>
                  <Primary>
                    <strong>back</strong>
                  </Primary>
                </Button>
              </div>
            </Padding>
          </Container>

          <Container>
            <Padding>
              <Preview
                mimeType={file.mimeType}
                previewUri={file.reader}
                title={title}
                description={description}
                tags={tags}
              />
            </Padding>
          </Container>

          <Container>
            <Padding>
              <Button onClick={handleHelper} fit>
                <Curate>mint OBJKT</Curate>
              </Button>
            </Padding>
          </Container>

          <Container>
            <Padding>
              <p>this operation costs {window.tz.mintPrice}~ tez</p>
              <p>Your royalties upon each sale are {royalties}%</p>
            </Padding>
          </Container>
        </>
      )}
    </Page>
  )
}

const flower1 = "data:image/png;base64,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";
const flower2 = "data:image/png;base64,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";
const flower3 = "data:image/png;base64,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";

// https://stackoverflow.com/a/12300351/1010653
function dataURItoBlob(dataURI) {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  var byteString = atob(dataURI.split(',')[1]);

  // separate out the mime component
  var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

  // write the bytes of the string to an ArrayBuffer
  var ab = new ArrayBuffer(byteString.length);

  // create a view into the buffer
  var ia = new Uint8Array(ab);

  // set the bytes of the buffer to the correct values
  for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
  }

  // write the ArrayBuffer to a blob, and you're done
  var blob = new Blob([ab], {type: mimeString});
  return {"buffer": ia, "blob": blob};
}

const objkt_file = {
        "title": "Upload OBJKT",
        "mimeType": "image/png",
        "reader": flower3
      };

window.tz = window.tz || {};

window.tz.setup_mint_mock_data = function() {
  console.log("Setting up some mock data");
  // setup data here
  let converted = dataURItoBlob(objkt_file.reader);
  objkt_file.mimeType = "image/png";
  objkt_file.buffer = converted.buffer;
  objkt_file.file = converted.blob;

  window.tz.mintData = {
    "title": "Flower 3",
    "description": "This is the worst flower yet",
    "tags": "flower,boring,meh",
    "royalties": 10,
    "file": objkt_file,
    "mint_fee": 0.02
  }
}

window.tz.mint_metropolis = function() {
  console.log("Running mint_metropolis");
  document.getElementById("tzHelperButton").firstElementChild.click();
}